// Founder review detail — ported from `frontend/app/approvals/[id].tsx`
// (task-23 brief §6 item 1: CANNED_REASONS / STATUS_BADGE, the 409
// no_host_agreement override flow). KYC docs render from `detail.kycDocs`
// directly (signed URLs) rather than mapping against a required-doc-types
// table (`kycDocTypesFor`, mobile-only, not ported) — a founder reviewing on
// a desk PC sees every uploaded doc either way; only the "missing" chip that
// mobile shows for a not-yet-uploaded required doc is not reproduced here.
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Fragment, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth/authContext';
import { opsDB, type BusinessDetail, type ReviewAction } from '@/lib/data/ops';
import { toReason } from '@/lib/api/reason';
import { cancelThenInvalidate } from '@/lib/query/refresh';
import { PageLoading } from '@/ui/Loader';

export const Route = createFileRoute('/approvals/$id')({
  component: ApprovalDetail,
});

const CANNED_REASONS = [
  'ID document unreadable',
  'Business photos missing',
  'Payout name mismatch',
  'NTB certificate required',
];

const STATUS_BADGE: Record<string, { className: string; label: string }> = {
  pending: { className: 'bg-tint-warning text-warning', label: 'Awaiting review' },
  approved: { className: 'bg-tint-success text-success', label: 'Live' },
  rejected: { className: 'bg-tint-warning text-danger', label: 'Changes requested' },
  info_requested: { className: 'bg-tint-info text-ink', label: 'Info requested' },
  suspended: { className: 'bg-tint-warning text-danger', label: 'Suspended' },
};

const SHEET_TITLES: Record<ReviewAction, { title: string; cta: string }> = {
  reject: { title: 'Request changes', cta: 'Send to owner' },
  request_info: { title: 'Request information', cta: 'Send to owner' },
  suspend: { title: 'Suspend business', cta: 'Suspend' },
};

function ApprovalDetail() {
  const { id } = Route.useParams();
  const { supabase } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [sheetAction, setSheetAction] = useState<ReviewAction | null>(null);
  const [reasonInput, setReasonInput] = useState('');
  const [rejectDocs, setRejectDocs] = useState<string[]>([]);
  const [rejectPayout, setRejectPayout] = useState(false);
  const [pendingOverride, setPendingOverride] = useState<{
    error: Error;
    issue: 'missing' | 'outdated';
    signedVersion: string | null;
    requiredVersion: string | null;
  } | null>(null);
  const [approveReason, setApproveReason] = useState('');
  // Fix round 1, item 4: Approve is blocked until every KYC doc has actually
  // been opened (a "View" click) — kycReviewed then genuinely means "what
  // the admin reviewed", not "what happened to be listed".
  const [openedDocIds, setOpenedDocIds] = useState<Set<string>>(new Set());

  const detailQuery = useQuery({
    queryKey: ['ops-pending', 'detail', id],
    queryFn: async (): Promise<BusinessDetail> => {
      const { data, error } = await opsDB.getDetail(supabase, id);
      if (error || !data) throw error ?? new Error('Not found');
      return data;
    },
  });

  const approveMutation = useMutation({
    mutationFn: async (opts?: { overrideNoAgreement?: boolean }) => {
      const d = detailQuery.data!;
      const reason = toReason(approveReason);
      if (!reason) throw new Error('A reason of at least 10 characters is required.');
      // Fix round 2, item 5: a doc with no signedUrl has nothing to review —
      // that's a real gap (a missing/purged file), not something to wave
      // through as "trivially opened". Block with a clear message instead.
      const unreviewable = d.kycDocs.filter((doc) => !doc.signedUrl);
      if (unreviewable.length > 0) {
        throw new Error(
          `${unreviewable.length} KYC document(s) have no file to review (${unreviewable.map((doc) => doc.doc_type).join(', ')}) — resolve this before approving.`,
        );
      }
      if (!d.kycDocs.every((doc) => doc.id && openedDocIds.has(doc.id))) {
        throw new Error('Open every KYC document before approving.');
      }
      // Only the documents this screen actually showed AND the admin opened
      // are approved/purged — see ops.ts's approve() header comment.
      // kycReviewed is built from openedDocIds itself (not "every doc"), so
      // it can never include one that wasn't actually marked opened.
      return opsDB.approve(
        supabase,
        d.id,
        d.ownerId,
        reason,
        d.kycDocs
          .filter((doc) => doc.id && openedDocIds.has(doc.id))
          .map((doc) => ({ id: doc.id, filePath: doc.filePath ?? null })),
        opts,
      );
    },
    onSuccess: (res) => {
      if (res.needsAgreementOverride) {
        setPendingOverride({
          error: res.error!,
          issue: res.agreementIssue ?? 'missing',
          signedVersion: res.signedVersion ?? null,
          requiredVersion: res.requiredVersion ?? null,
        });
        return;
      }
      if (res.error) throw res.error;
      cancelThenInvalidate(queryClient, { queryKey: ['ops-pending'] });
      void navigate({ to: '/approvals' });
    },
  });

  const reviewMutation = useMutation({
    mutationFn: async () => {
      const d = detailQuery.data!;
      if (!sheetAction) throw new Error('No action selected');
      const reason = toReason(reasonInput);
      if (!reason) throw new Error('A reason of at least 10 characters is required — the owner will see this.');
      const { error } = await opsDB.review(supabase, {
        action: sheetAction,
        businessId: d.id,
        ownerId: d.ownerId,
        reason,
        rejectDocTypes: sheetAction === 'reject' ? rejectDocs : undefined,
        rejectPayout: sheetAction === 'reject' ? rejectPayout : undefined,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setSheetAction(null);
      cancelThenInvalidate(queryClient, { queryKey: ['ops-pending'] });
      void navigate({ to: '/approvals' });
    },
  });

  if (detailQuery.isLoading) return <PageLoading />;
  if (detailQuery.isError || !detailQuery.data) {
    return (
      <section className="rounded-card border border-border bg-surface p-xl shadow-card">
        <h1 className="text-screen-title font-extrabold tracking-tight text-ink">Couldn't load this business</h1>
        <button type="button" onClick={() => void detailQuery.refetch()} className="mt-sm text-body text-canyon underline">
          Retry
        </button>
      </section>
    );
  }

  const detail = detailQuery.data;
  const status = STATUS_BADGE[detail.reviewStatus] ?? { className: 'bg-tint-warning text-warning', label: 'Awaiting review' };
  const allDocsHaveIds = detail.kycDocs.every((d) => typeof d.id === 'string');
  // Fix round 2, item 5: a doc with no signedUrl CANNOT be reviewed (the
  // storage object is missing/purged) — that blocks approval outright, with
  // a clear reason, rather than being waved through as "trivially opened".
  const unreviewableDocs = detail.kycDocs.filter((d) => !d.signedUrl);
  const allDocsOpened = detail.kycDocs.every((d) => d.signedUrl && d.id && openedDocIds.has(d.id));
  const canApprove = !!toReason(approveReason) && allDocsHaveIds && unreviewableDocs.length === 0 && allDocsOpened;
  // TASKS 109: a disabled Approve with only a hover title read as "broken" —
  // say on screen exactly what is still missing.
  const openedCount = detail.kycDocs.filter((d) => d.id && openedDocIds.has(d.id)).length;
  const approveBlockers: string[] = [];
  if (unreviewableDocs.length > 0) approveBlockers.push('some KYC documents have no file to review');
  else if (!allDocsOpened) approveBlockers.push(`open every KYC document (${openedCount} of ${detail.kycDocs.length} opened)`);
  if (!toReason(approveReason)) approveBlockers.push('write an approval reason (at least 10 characters)');

  function openSheet(action: ReviewAction) {
    setReasonInput('');
    setRejectDocs([]);
    setRejectPayout(false);
    setSheetAction(action);
  }

  return (
    <section className="flex flex-col gap-lg">
      <div className="flex items-center gap-sm">
        <div className="flex-1">
          <h1 className="text-screen-title font-extrabold tracking-tight text-ink">{detail.name ?? 'Unnamed business'}</h1>
          <p className="mt-xs text-body text-ink-2">
            {(detail.category ?? 'uncategorised')}
            {detail.location ? ` · ${detail.location}` : ''}
          </p>
        </div>
        <span className={`rounded-chip px-sm py-xs text-caption font-semibold ${status.className}`}>{status.label}</span>
      </div>

      {detail.reviewReason ? (
        <div className="rounded-card border border-border bg-tint-warning p-lg">
          <p className="text-caption font-bold text-ink">Last review note</p>
          <p className="mt-xs text-body text-ink">{detail.reviewReason}</p>
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-lg md:grid-cols-2">
        <div className="rounded-card border border-border bg-surface p-lg shadow-card">
          <h2 className="text-h3 font-bold text-ink">Owner &amp; contact</h2>
          <dl className="mt-md grid grid-cols-2 gap-y-sm text-body">
            <dt className="text-ink-2">Owner</dt>
            <dd className="text-ink">{[detail.ownerName, detail.ownerEmail].filter(Boolean).join(' · ') || '—'}</dd>
            {detail.businessEmail ? (
              <>
                <dt className="text-ink-2">Business email</dt>
                <dd className="text-ink">{detail.businessEmail}</dd>
              </>
            ) : null}
            {detail.phone ? (
              <>
                <dt className="text-ink-2">Phone</dt>
                <dd className="text-ink">{detail.phone}</dd>
              </>
            ) : null}
            {detail.whatsapp ? (
              <>
                <dt className="text-ink-2">WhatsApp</dt>
                <dd className="text-ink">{detail.whatsapp}</dd>
              </>
            ) : null}
            {detail.ntbNumber ? (
              <>
                <dt className="text-ink-2">NTB number</dt>
                <dd className="text-ink">{detail.ntbNumber}</dd>
              </>
            ) : null}
          </dl>
        </div>

        <div className="rounded-card border border-border bg-surface p-lg shadow-card">
          <h2 className="text-h3 font-bold text-ink">Settlement details</h2>
          {detail.payout ? (
            <dl className="mt-md grid grid-cols-2 gap-y-sm text-body">
              {Object.entries(detail.payout)
                .filter(([, v]) => v)
                .map(([k, v]) => (
                  <Fragment key={k}>
                    <dt className="text-ink-2">{k.replace(/_/g, ' ')}</dt>
                    <dd className="text-ink">{String(v)}</dd>
                  </Fragment>
                ))}
            </dl>
          ) : (
            <p className="mt-sm text-body text-ink-2">No settlement details on record yet.</p>
          )}
        </div>
      </div>

      {detail.description ? (
        <div className="rounded-card border border-border bg-surface p-lg shadow-card">
          <p className="text-body text-ink">{detail.description}</p>
        </div>
      ) : null}

      <div className="rounded-card border border-border bg-surface p-lg shadow-card">
        <h2 className="text-h3 font-bold text-ink">KYC documents</h2>
        {unreviewableDocs.length > 0 ? (
          <p className="mt-sm rounded-button bg-tint-warning p-sm text-body text-danger">
            {unreviewableDocs.length} document(s) have no file to review (
            {unreviewableDocs.map((d) => d.doc_type.replace(/_/g, ' ')).join(', ')}) — cannot approve until this is
            resolved (re-upload, or check the storage bucket).
          </p>
        ) : null}
        {detail.kycDocs.length === 0 ? (
          <p className="mt-sm text-body text-ink-2">No documents uploaded.</p>
        ) : (
          <ul className="mt-md flex flex-col gap-sm">
            {detail.kycDocs.map((d) => (
              <li key={d.doc_type} className="flex items-center justify-between gap-sm border-b border-hair pb-sm">
                <span className="text-body text-ink">{d.doc_type.replace(/_/g, ' ')}</span>
                <span className="flex items-center gap-sm">
                  <span
                    className={`rounded-chip px-sm py-xs text-caption font-semibold ${
                      d.status === 'approved'
                        ? 'bg-tint-success text-success'
                        : d.status === 'rejected'
                          ? 'bg-tint-warning text-danger'
                          : 'bg-tint-warning text-warning'
                    }`}
                  >
                    {d.status}
                  </span>
                  {d.signedUrl ? (
                    <a
                      href={d.signedUrl}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => {
                        if (d.id) setOpenedDocIds((prev) => new Set(prev).add(d.id!));
                      }}
                      className="text-caption font-semibold text-canyon underline"
                    >
                      {d.id && openedDocIds.has(d.id) ? 'Opened ✓' : 'View'}
                    </a>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {detail.photoUrls.length > 0 ? (
        <div className="flex flex-wrap gap-sm">
          {detail.photoUrls.map((url) => (
            <a key={url} href={url} target="_blank" rel="noreferrer">
              <img src={url} alt="" className="h-24 w-24 rounded-card object-cover" />
            </a>
          ))}
        </div>
      ) : null}

      {pendingOverride ? (
        <div className="rounded-card border border-danger bg-tint-warning p-lg">
          <p className="text-h3 font-bold text-ink">
            {pendingOverride.issue === 'outdated'
              ? `Host Agreement out of date (signed v${pendingOverride.signedVersion}, current v${pendingOverride.requiredVersion})`
              : 'No Host Agreement on record'}
          </p>
          <p className="mt-xs text-body text-ink">{pendingOverride.error.message}</p>
          <div className="mt-md flex gap-sm">
            <button
              type="button"
              onClick={() => setPendingOverride(null)}
              className="rounded-button border border-border px-md py-xs text-body text-ink"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={approveMutation.isPending || !canApprove}
              onClick={() => {
                setPendingOverride(null);
                approveMutation.mutate({ overrideNoAgreement: true });
              }}
              className="rounded-button bg-danger px-md py-xs text-body font-semibold text-white disabled:opacity-50"
            >
              {pendingOverride.issue === 'outdated'
                ? 'I have a signed copy of the current agreement — override'
                : 'I have a signed agreement — override'}
            </button>
          </div>
        </div>
      ) : null}

      <div className="flex flex-col gap-sm">
        <label className="text-caption font-semibold text-ink-2" htmlFor="approve-reason">
          Approval reason (recorded in the audit log)
        </label>
        <textarea
          id="approve-reason"
          value={approveReason}
          onChange={(e) => setApproveReason(e.target.value)}
          placeholder="e.g. KYC docs and NTB number verified against BIPA."
          className="min-h-[64px] rounded-button border border-border bg-surface p-sm text-body text-ink"
        />
        <div className="flex flex-wrap gap-sm">
          {detail.isApproved ? (
            <button
              type="button"
              disabled={reviewMutation.isPending}
              onClick={() => openSheet('suspend')}
              className="rounded-button bg-danger px-md py-sm text-body font-semibold text-white disabled:opacity-50"
            >
              Suspend business
            </button>
          ) : (
            <>
              <button
                type="button"
                disabled={approveMutation.isPending || !canApprove}
                onClick={() => approveMutation.mutate(undefined)}
                title={
                  unreviewableDocs.length > 0
                    ? 'Some KYC documents have no file to review — resolve that first'
                    : !allDocsOpened
                      ? 'Open every KYC document before approving'
                      : undefined
                }
                className="rounded-button bg-canyon px-md py-sm text-body font-semibold text-white disabled:opacity-50"
              >
                Approve
              </button>
              <button
                type="button"
                onClick={() => openSheet('reject')}
                className="rounded-button border border-border px-md py-sm text-body text-ink"
              >
                Request changes
              </button>
              <button
                type="button"
                onClick={() => openSheet('request_info')}
                className="rounded-button border border-border px-md py-sm text-body text-ink"
              >
                Request info
              </button>
            </>
          )}
        </div>
        {!detail.isApproved && approveBlockers.length > 0 ? (
          <p className="text-caption text-ink-2" data-testid="approve-blockers">
            To approve: {approveBlockers.join(' · ')}.
          </p>
        ) : null}
        {approveMutation.isError ? (
          <p className="text-body text-danger">{(approveMutation.error as Error).message}</p>
        ) : null}
      </div>

      {sheetAction ? (
        <div className="rounded-card border border-border bg-surface p-lg shadow-card">
          <h2 className="text-h3 font-bold text-ink">{SHEET_TITLES[sheetAction].title}</h2>
          <p className="mt-xs text-caption text-ink-2">The owner sees this reason in the app and by email.</p>
          <div className="mt-md flex flex-wrap gap-sm">
            {CANNED_REASONS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setReasonInput((r) => (r ? `${r.replace(/[.\s]*$/, '')}. ${c}` : c))}
                className="rounded-chip bg-surface-2 px-md py-xs text-caption text-ink"
              >
                {c}
              </button>
            ))}
          </div>
          <textarea
            value={reasonInput}
            onChange={(e) => setReasonInput(e.target.value)}
            placeholder="Reason (required)…"
            className="mt-md min-h-[88px] w-full rounded-button border border-border bg-surface p-sm text-body text-ink"
          />
          {sheetAction === 'reject' && (detail.kycDocs.length > 0 || detail.payout) ? (
            <div className="mt-md">
              <p className="text-caption font-bold text-ink">Mark as rejected (owner must re-upload)</p>
              {detail.kycDocs.map((d) => {
                const selected = rejectDocs.includes(d.doc_type);
                return (
                  <label key={d.doc_type} className="mt-xs flex items-center gap-sm text-body text-ink">
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() =>
                        setRejectDocs((prev) => (selected ? prev.filter((x) => x !== d.doc_type) : [...prev, d.doc_type]))
                      }
                    />
                    {d.doc_type.replace(/_/g, ' ')}
                  </label>
                );
              })}
              {detail.payout ? (
                <label className="mt-xs flex items-center gap-sm text-body text-ink">
                  <input type="checkbox" checked={rejectPayout} onChange={() => setRejectPayout((v) => !v)} />
                  Settlement details
                </label>
              ) : null}
            </div>
          ) : null}
          {reviewMutation.isError ? (
            <p className="mt-sm text-body text-danger">{(reviewMutation.error as Error).message}</p>
          ) : null}
          <div className="mt-md flex gap-sm">
            <button
              type="button"
              onClick={() => setSheetAction(null)}
              className="rounded-button border border-border px-md py-sm text-body text-ink"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={reviewMutation.isPending || !toReason(reasonInput)}
              onClick={() => reviewMutation.mutate()}
              className={`rounded-button px-md py-sm text-body font-semibold text-white disabled:opacity-50 ${
                sheetAction === 'suspend' ? 'bg-danger' : 'bg-canyon'
              }`}
            >
              {SHEET_TITLES[sheetAction].cta}
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
